import { Transition } from "@headlessui/react";
import { XCircleIcon } from "@heroicons/react/20/solid";
import { CheckCircleIcon, ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import { classNames } from "~/utils";
import React, { Fragment, useContext, useState } from "react";
import { graphql } from "~/graphql/codegen";
import { Trans } from "@lingui/react/macro";
import { getServerClient } from "~/graphql";
import type {
  AddResourceFromFileMutation,
  AddResourceFromFileMutationVariables,
  FilePrepareMutation,
  FilePrepareMutationVariables,
} from "~/graphql/codegen/graphql";

const ADD_RESOURCE_FROM_FILE = graphql(`
  mutation addResourceFromFile($fileID: String!, $fileName: String!, $urgent: Boolean) {
    addResourceFromFile(body: { fileID: $fileID, fileName: $fileName, urgent: $urgent }) {
      id
    }
  }
`);

const FILE_PREPARE = graphql(`
  mutation filePrepare($contentType: String!) {
    filePrepare(contentType: $contentType) {
      fileID
      url
      fileDisplayUrl
    }
  }
`);

export const UploadContext = React.createContext<{
  upload: (file: File, createResource: boolean) => Promise<any>;
}>({
  upload: async (file: File, createResource: boolean) => {},
});
interface FileUpload {
  file: File;
  progress: number;
  state: "started" | "starting" | "error" | "completed" | "hidden";
  id: string;
}
export default function UploadManager(props: React.PropsWithChildren<{}>) {
  const [uploads, setUploads] = useState<FileUpload[]>([]);

  const uploadFile = (file: File, url: string, onProgress: (n: number) => void) => {
    return new Promise<any>((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // listen for `load` event
      xhr.onload = () => {
        resolve(null);
      };

      // listen for `error` event
      xhr.onerror = (event) => {
        reject("upload failed " + xhr.status);
      };

      // listen for `abort` event
      xhr.onabort = () => {
        reject("upload failed " + xhr.status);
      };

      // listen for `progress` event
      xhr.upload.onprogress = (event) => {
        // event.loaded returns how many bytes are downloaded
        // event.total returns the total number of bytes
        // event.total is only available if server sends `Content-Length` header
        onProgress(event.loaded / event.total);
      };
      xhr.open("PUT", url);
      xhr.send(file);
    });
  };
  const visibleUploads = uploads.filter((x) => x.state !== "hidden");

  const client = getServerClient();

  const getFileUploadInfo = (fileType: string) => {
    return client.mutate<FilePrepareMutation, FilePrepareMutationVariables>({
      mutation: FILE_PREPARE,
      variables: {
        contentType: fileType,
      },
    });
  };

  const saveResourceFromFile = (fileInfo: { name: string; file_id: string; urgent?: boolean }) => {
    return client.mutate<AddResourceFromFileMutation, AddResourceFromFileMutationVariables>({
      mutation: ADD_RESOURCE_FROM_FILE,
      variables: {
        fileID: fileInfo.file_id,
        fileName: fileInfo.name,
        urgent: fileInfo.urgent,
      },
    });
  };

  return (
    <UploadContext.Provider
      value={{
        upload: async (file, createResource) => {
          const id = new Date().getDate() * Math.random() + "_str"; // make sure it's a string, don't trust number in JS

          setUploads((prev) => [...prev, { file, progress: 0, id: id, state: "starting" }]);

          try {
            const info = await getFileUploadInfo(file.type);
            if (!info.data?.filePrepare?.url || !info.data?.filePrepare?.fileID) {
              setUploads((prev) =>
                prev.map((x) => {
                  if (x.id !== id) {
                    return x;
                  }
                  return { ...x, state: "error" };
                }),
              );
              return;
            }
            await uploadFile(file, info.data.filePrepare.url, (p) => {
              setUploads((prev) =>
                prev.map((x) => {
                  if (x.id !== id) {
                    return x;
                  }
                  return { ...x, state: "started", progress: p };
                }),
              );
            });

            let resourceID = "";
            if (createResource) {
              const saveResp = await saveResourceFromFile({
                name: file.name,
                file_id: info.data.filePrepare.fileID,
              });

              resourceID = saveResp.data?.addResourceFromFile.id || "";
            }

            setUploads((prev) =>
              prev.map((x) => {
                if (x.id !== id) {
                  return x;
                }
                return { ...x, state: "completed", progress: 1 };
              }),
            );

            // Give some time for the backend and elastic...
            setTimeout(() => {
              const event = new Event("refresh_library");
              document.dispatchEvent(event);
            }, 300);

            //Hide the uploaded from the list.
            setTimeout(() => {
              setUploads((prev) =>
                prev.map((x) => {
                  if (x.id !== id) {
                    return x;
                  }
                  return { ...x, state: "hidden", progress: 1 };
                }),
              );
              setTimeout(() => {
                setUploads((prev) => prev.filter((x) => x.id !== id));
              }, 500);
            }, 3000);

            return resourceID || info.data?.filePrepare.fileID;
          } catch (error) {
            console.error("error uploading file: ", error);
            setUploads((prev) =>
              prev.map((x) => {
                if (x.id !== id) {
                  return x;
                }
                return { ...x, state: "error" };
              }),
            );
          }
        },
      }}
    >
      {props.children}
      <div className="right-4 fixed bottom-4 flex items-end flex-col space-y-2 z-40">
        {visibleUploads.map((f, i) => (
          <div
            className="w-full max-w-sm flex flex-col items-center space-y-4 sm:items-end"
            key={f.file.name + "_" + i}
          >
            {/* Notification panel, dynamically insert this into the live region when it needs to be displayed */}
            {/* eslint-disable lingui/no-unlocalized-strings -- headlessui Transition CSS class props */}
            <Transition
              show={f.state !== "hidden"}
              as={Fragment}
              enter="transform ease-out duration-300 transition"
              enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
              enterTo="translate-y-0 opacity-100 sm:translate-x-0"
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
            {/* eslint-enable lingui/no-unlocalized-strings */}
              <div className="w-full bg-background shadow-lg rounded-lg pointer-events-auto ring-1 ring-black/5 overflow-hidden">
                <div className="w-full rounded-md h-1">
                  <div
                    className={classNames("bg-primary", "h-1 rounded-md ease-linear ")}
                    style={{
                      width: `${Math.round(f.progress * 100)}%`,
                      transition: `width 0.1s`,
                    }}
                  ></div>
                </div>
                <div className="p-4">
                  <div className="flex items-start">
                    <div className="shrink-0">
                      {(f.state === "started" || f.state === "starting") && (
                        <ArrowUpTrayIcon className={classNames("h-6 w-6 text-primary")} aria-hidden="true" />
                      )}
                      {f.state === "completed" && (
                        <CheckCircleIcon className={classNames("h-6 w-6 text-primary")} aria-hidden="true" />
                      )}
                      {f.state === "error" && (
                        <XCircleIcon className={classNames("h-6 w-6 text-destructive")} aria-hidden="true" />
                      )}
                    </div>
                    <div className="ml-3 w-full flex-1 pt-0.5">
                      <p className="text-sm font-medium text-muted-foreground line-clamp-1">
                        {(f.state === "started" || f.state === "starting") && <Trans>Uploading {f.file.name}</Trans>}
                        {f.state === "completed" && <Trans>{f.file.name} uploaded</Trans>}
                        {f.state === "error" && <Trans>Failed to upload {f.file.name}</Trans>}
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">{Math.round(f.progress * 100)}%</p>

                      <div className="mt-3 flex space-x-7"></div>
                    </div>
                  </div>
                </div>
              </div>
            </Transition>
          </div>
        ))}
      </div>
    </UploadContext.Provider>
  );
}

export const useUpload = () => useContext(UploadContext);
